import { useState, useEffect, useRef } from 'react';

export function useLearningPack(packId) {
  const storageKey = `learningPack_${packId}_progress`;
  const instanceIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`
  );
  
  // Load initial state from localStorage
  const loadProgress = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Failed to load learning pack progress:', error);
      return {};
    }
  };

  const [completedItems, setCompletedItems] = useState(loadProgress);
  const [reloadTrigger, setReloadTrigger] = useState(0); // Trigger to force reload from localStorage

  // Reload from localStorage when trigger changes
  useEffect(() => {
    if (reloadTrigger > 0) {
      const reloaded = loadProgress();
      setCompletedItems(reloaded);
      console.log(`🔄 Reloaded progress from localStorage (trigger: ${reloadTrigger}):`, reloaded);
    }
    // Only depend on reloadTrigger, not storageKey to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  // Keep multiple useLearningPack() instances in sync within the same tab.
  // localStorage "storage" events do not fire in the same document that made the change.
  useEffect(() => {
    const handleProgressChanged = (event) => {
      const detail = event?.detail || {};
      if (detail.packId !== packId) return;
      if (detail.instanceId && detail.instanceId === instanceIdRef.current) return;
      // Another instance updated this pack's progress — reload.
      reloadFromStorage();
    };

    window.addEventListener('learningPackProgressChanged', handleProgressChanged);
    return () => {
      window.removeEventListener('learningPackProgressChanged', handleProgressChanged);
    };
    // Intentionally exclude reloadFromStorage from deps; it's stable enough for our use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  // Save to localStorage whenever completedItems changes
  // Use a ref to track if we're in the middle of a reload to prevent loops
  const isReloadingRef = useRef(false);
  
  useEffect(() => {
    // Skip save if we're in the middle of a reload
    if (isReloadingRef.current) {
      isReloadingRef.current = false;
      return;
    }
    
    try {
      // Only save if there are completed items (don't save empty object)
      if (Object.keys(completedItems).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(completedItems));
        console.log(`💾 Saved progress to localStorage: ${storageKey}`, completedItems);
      } else {
        // If empty, remove the key to ensure clean state
        localStorage.removeItem(storageKey);
        console.log(`🗑️ Removed empty progress from localStorage: ${storageKey}`);
      }

      // Notify other in-page listeners (e.g., RightPanel vs TextbookViewer) to refresh.
      window.dispatchEvent(
        new CustomEvent('learningPackProgressChanged', {
          detail: {
            packId,
            storageKey,
            instanceId: instanceIdRef.current,
          },
        })
      );
    } catch (error) {
      console.error('Failed to save learning pack progress:', error);
    }
    // Only depend on completedItems, not storageKey to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedItems]);

  // Check if an item is completed
  const isItemCompleted = (itemId) => {
    // Ensure itemId is a string for consistent key matching
    const itemKey = String(itemId);
    return !!completedItems[itemKey];
  };

  // Complete an item (only if not already completed)
  const completeItem = (itemId, xpReward) => {
    // Ensure itemId is a string for consistent key matching
    const itemKey = String(itemId);
    
    if (!completedItems[itemKey]) {
      setCompletedItems(prev => ({
        ...prev,
        [itemKey]: {
          completedAt: new Date().toISOString(),
          xpEarned: xpReward
        }
      }));
      console.log(`📝 Learning Pack (${packId}): Marked item ${itemKey} as completed. XP: ${xpReward}`);
      return xpReward; // Return XP earned for this completion
    }
    console.log(`⚠️ Learning Pack (${packId}): Item ${itemKey} already completed. Skipping duplicate reward.`);
    return 0; // Already completed, no XP earned
  };

  // Get total XP earned from all completed items
  const getTotalXP = () => {
    return Object.values(completedItems).reduce((sum, item) => sum + (item.xpEarned || 0), 0);
  };

  // Get count of completed items
  const getCompletedCount = () => {
    return Object.keys(completedItems).length;
  };

  // Check if all items in the pack are completed
  const isPackCompleted = (totalItems) => {
    return Object.keys(completedItems).length >= totalItems;
  };

  // Reset all progress (useful for testing)
  const resetProgress = () => {
    console.log('🔄 useLearningPack.resetProgress() called for packId:', packId, 'storageKey:', storageKey);
    setCompletedItems({});
    try {
      localStorage.removeItem(storageKey);
      console.log('✅ Removed localStorage key:', storageKey);
    } catch (error) {
      console.error('❌ Error removing localStorage key:', error);
    }
    // Trigger reload to ensure state is synced
    setReloadTrigger(prev => prev + 1);
    return {}; // Return empty object for consistency
  };

  // Force reload from localStorage (useful after external reset)
  const reloadFromStorage = () => {
    isReloadingRef.current = true; // Mark that we're reloading
    setReloadTrigger(prev => prev + 1);
  };

  return {
    completedItems,
    isItemCompleted,
    completeItem,
    getTotalXP,
    getCompletedCount,
    isPackCompleted,
    resetProgress,
    reloadFromStorage
  };
}
